import React, { useCallback, useState, type MouseEventHandler } from "react";

import { Button, Form, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import { api } from "../utils/api";

import { getUploadFileCustomErrorMessage } from "../utils/errorMessages";
import { validateFile } from "../utils/attackLibraryValidation";

const initialRenderTime = Date.now();

export function FileUploadInputGroup({
  token,
  onFileUploaded,
}: {
  token: string;
  // eslint-disable-next-line no-unused-vars
  onFileUploaded?: (file: File) => void;
}) {
  const [inputFileKey, setInputFileKey] = useState(initialRenderTime);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      e.target.value = ""; // Reset file input
      setUploadedFile(null);
      return;
    }

    setUploadedFile(file);
  }, []);

  const uploadFile = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      if (!uploadedFile) {
        return toast.error("Please select a file to upload");
      }

      e.preventDefault();
      const formData = new FormData();
      formData.append("file", uploadedFile);

      api
        .uploadFile(token, formData)
        .then((res) => {
          toast.success(res?.message); // FUTURE: Consider using a frontend driven message here
          try {
            onFileUploaded?.(uploadedFile);
          } catch (err) {
            console.error("onFileUploaded callback failed", err);
          }
          setInputFileKey(Date.now());
          setUploadedFile(null);
        })
        .catch((err) => {
          toast.error(getUploadFileCustomErrorMessage(err));
        });
    },
    [token, uploadedFile, onFileUploaded]
  );

  return (
    <InputGroup size="sm">
      <Form.Control
        key={inputFileKey}
        placeholder="Add new file"
        type="file"
        accept=".7z"
        multiple={false}
        name="uploadDocs"
        onChange={handleChange}
      ></Form.Control>
      <Button
        variant="dark"
        onClick={uploadFile}
        disabled={!uploadedFile}
        className="input-group-text"
      >
        Upload
      </Button>
    </InputGroup>
  );
}
